import { renewAzureSatelliteAccess } from './azure-satellite.js';
import { isString } from '../shared/runtime-values.js';
import type { ErrorEvent, Map, RasterTileSource } from 'maplibre-gl';

export function isSatelliteAccessError(error: ErrorEvent['error']): boolean {
  return 'status' in error && error.status === 401 && 'url' in error && isString(error.url)
    && new URL(error.url, 'https://drawonamap.com').pathname === '/api/azure-satellite';
}

/** Retry the current globe sources once per minute, including after cookie loss. */
export function installGlobeSatelliteRecovery(map: Map): void {
  type RecoveryState = { status: 'idle' | 'pending'; retryAfter: number } | { status: 'stopped' };
  let state: RecoveryState = { status: 'idle', retryAfter: 0 };
  const recover = ({ error }: ErrorEvent) => {
    if (!isSatelliteAccessError(error)) { console.error(error); return; }
    if (state.status !== 'idle' || Date.now() < state.retryAfter) return;
    const sources = ['basemap', 'basemap:labels'].flatMap(id => {
      const source = map.getSource<RasterTileSource>(id);
      return source?.type === 'raster' && source.tiles?.some(url => url.startsWith('/api/azure-satellite?'))
        ? [{ id, source }] : [];
    });
    if (!sources.length) return;
    state = { status: 'pending', retryAfter: Date.now() + 60_000 };
    void renewAzureSatelliteAccess().then(() => {
      if (state.status === 'stopped') return;
      for (const { id, source } of sources) {
        if (map.getSource(id) === source) source.setTiles([...source.tiles]);
      }
    }).catch(error => { if (state.status !== 'stopped') console.warn('Globe satellite access failed:', error); })
      .finally(() => { if (state.status === 'pending') state = { ...state, status: 'idle' }; });
  };
  const stop = () => {
    state = { status: 'stopped' };
    map.off('error', recover);
    map.off('remove', stop);
  };
  map.on('error', recover);
  map.on('remove', stop);
}
